import React from 'react'
import { useSimpleAuth  } from '../../features/auth/SimpleAuthContext'
import { hasPermission, hasMinimumRole, USER_ROLES } from '../../services/roleService'
import { Lock } from 'lucide-react'

/**
 * Higher-Order Component para proteger rutas basado en permisos
 * @param {string|number} requiredPermission - Permiso requerido o nivel mínimo de rol
 * @param {boolean} requireAuth - Si requiere autenticación (default: true)
 * @param {React.Component} Component - Componente a proteger
 */
const ProtectedRoute = ({
  requiredPermission,
  requireAuth = true,
  component: Component,
  ...rest
}) => {
  const { currentUser } = useSimpleAuth()
  const [hasAccess, setHasAccess] = React.useState(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    checkAccess()
  }, [currentUser, requiredPermission, requireAuth])

  const checkAccess = async () => {
    try {
      setLoading(true)

      // SECURITY: Verificar autenticación primero
      if (requireAuth && !currentUser) {
        console.warn('⚠️ ProtectedRoute: Usuario no autenticado')
        setHasAccess(false)
        return
      }

      // SECURITY: Verificar que currentUser tenga uid válido
      if (!currentUser?.uid || typeof currentUser.uid !== 'string') {
        console.warn('⚠️ ProtectedRoute: currentUser.uid inválido')
        setHasAccess(false)
        return
      }

      // Si no hay permisos requeridos y está autenticado, permitir acceso
      if (!requiredPermission && currentUser?.uid) {
        setHasAccess(true)
        return
      }

      // Verificar si es un nivel de rol o un permiso específico
      const isRoleLevel = typeof requiredPermission === 'number'

      if (isRoleLevel) {
        // Verificar nivel mínimo de rol
        const hasMinRole = await hasMinimumRole(currentUser.uid, requiredPermission)
        console.log(`🔒 Verificando acceso: usuario ${currentUser.uid} requiere rol ${requiredPermission}, tiene acceso: ${hasMinRole}`)
        setHasAccess(hasMinRole)
      } else {
        // Verificar permiso específico
        const hasPerm = await hasPermission(currentUser.uid, requiredPermission)
        console.log(`🔒 Verificando acceso: usuario ${currentUser.uid} requiere permiso ${requiredPermission}, tiene acceso: ${hasPerm}`)
        setHasAccess(hasPerm)
      }
    } catch (error) {
      console.error('❌ Error checking access permissions:', error)
      setHasAccess(false)
    } finally {
      setLoading(false)
    }
  }

  // Mostrar estado de carga
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{borderColor: '#3BC6C6'}}></div>
          <p style={{color: '#4A4A4A'}}>Verificando permisos...</p>
        </div>
      </div>
    )
  }

  // No tiene acceso
  if (!hasAccess) {
    // Determinar el mensaje según el nivel requerido
    const getAccessDeniedMessage = () => {
      if (requireAuth && !currentUser) {
        return 'Debes iniciar sesión para acceder a esta página'
      }

      if (!currentUser?.uid) {
        return 'Error de autenticación. Por favor, cierra sesión y vuelve a iniciar.'
      }

      // Mensajes específicos según el nivel de rol
      if (requiredPermission === USER_ROLES.ADMIN) {
        return 'Esta página es exclusiva para administradores. Necesitas rol de administrador (nivel 80) para acceder.'
      }
      if (requiredPermission === 60) {
        return 'Esta página es exclusiva para moderadores. Necesitas rol de moderador (nivel 60) para acceder.'
      }
      if (requiredPermission === 90) {
        return 'Esta página es exclusiva para super administradores.'
      }

      return 'No tienes los permisos necesarios para acceder a esta página'
    }

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#fff',
        backgroundImage: 'radial-gradient(#000 0.5px, transparent 0.5px)',
        backgroundSize: '24px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          border: '6px solid #000',
          boxShadow: '12px 12px 0px #000',
          background: '#fff',
          padding: '60px 40px',
          textAlign: 'center',
          maxWidth: '500px',
          width: '100%'
        }}>
          {/* Icon */}
          <div style={{
            background: '#000',
            border: '4px solid #000',
            width: '80px',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '6px 6px 0px #000'
          }}>
            <Lock size={40} strokeWidth={3} style={{ color: '#FF2E63' }} />
          </div>

          {/* Title */}
          <h2 style={{
            fontFamily: 'Lexend Mega, sans-serif',
            textTransform: 'uppercase',
            fontSize: '2rem',
            margin: '0 0 16px 0',
            color: '#000',
            lineHeight: 1
          }}>
            Acceso Denegado
          </h2>

          {/* Description */}
          <p style={{
            fontWeight: 700,
            margin: '0 0 32px 0',
            fontSize: '1rem',
            color: '#4A4A4A',
            lineHeight: 1.5
          }}>
            {getAccessDeniedMessage()}
          </p>

          {/* Button */}
          <button
            onClick={() => window.history.back()}
            style={{
              padding: '16px 32px',
              background: '#FFFFFF',
              color: '#000000',
              border: '4px solid #000',
              borderRadius: '0',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: '0.9rem',
              cursor: 'pointer',
              boxShadow: '6px 6px 0px #000',
              transition: 'transform 0.15s ease, boxShadow 0.15s ease',
              fontFamily: 'Lexend Mega, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)'
              e.currentTarget.style.boxShadow = '8px 8px 0px #000'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)'
              e.currentTarget.style.boxShadow = '6px 6px 0px #000'
            }}
          >
            Volver
          </button>
        </div>
      </div>
    )
  }

  // Tiene acceso, renderizar el componente
  return <Component {...rest} />
}

// Componentes de protección específicos para facilitar uso

export const AdminRoute = (props) => (
  <ProtectedRoute
    requiredPermission={USER_ROLES.ADMIN}
    component={props.component}
    {...props}
  />
)

// ModeratorRoute requiere nivel 60 (no definido en USER_ROLES, usar valor directo)
export const ModeratorRoute = (props) => (
  <ProtectedRoute
    requiredPermission={60}
    component={props.component}
    {...props}
  />
)

// SuperAdminRoute requiere nivel 90 (no definido en USER_ROLES, usar valor directo)
export const SuperAdminRoute = (props) => (
  <ProtectedRoute
    requiredPermission={90}
    component={props.component}
    {...props}
  />
)

// Protección por permisos específicos
export const PermissionRoute = ({ permission, ...props }) => (
  <ProtectedRoute
    requiredPermission={permission}
    component={props.component}
    {...props}
  />
)

export default ProtectedRoute